import { fetchStoresAmasty } from "./magento-helper";

/**
 * Amasty Store Locator API functions
 * Uses GraphQL to fetch store locations from Magento
 * No Supabase auth required - direct Magento integration
 */

/**
 * Amasty store location interface
 */
export interface AmastyStoreLocation {
  id: string;
  name: string;
  full_name: string;
  country: string;
  city: string;
  zip: string;
  address: string;
  state: string;
  phone: string;
  email: string;
  lat: number;
  lng: number;
  url_key: string;
  show_schedule: boolean;
  schedule_string: string;
  working_time_today: string;
  distance?: number;
  main_image_name?: string;
  average_rating?: number;
  description?: string;
}

/**
 * Pagination info from Amasty response
 */
// interface PageInfo {
//   current_page: number;
//   page_size: number;
//   total_pages: number;
// }

/**
 * Response from Amasty GraphQL
 */
// interface AmastyResponse {
//   data?: {
//     searchAmStoreLocations?: {
//       items: AmastyStoreLocation[];
//       total_count: number;
//       page_info?: PageInfo;
//     };
//   };
//   errors?: Array<{ message: string }>;
// }

/**
 * Execute Amasty GraphQL query
 * @param baseUrl - The Magento store base URL
 * @param filter - Filter string for the query
 * @param pageSize - Number of results per page
 * @param currentPage - Page number
 * @returns GraphQL response
 */
// async function executeAmastyQuery(
//   baseUrl: string,
//   filter: string,
//   pageSize: number,
//   currentPage: number
// ): Promise<{stores: AmastyStoreLocation[]; totalNearbyStores: number; totalPages: number}> {
//   const url = `${baseUrl}/graphql`;

//   let query = `
//     {
//       searchAmStoreLocations(
//         filter: ${filter}, 
//         pageSize: ${pageSize}, 
//         currentPage: ${currentPage}
//       ) { 
//         items {
//           id
//           name
//           address
//           city
//           zip
//           phone
//           email
//           show_schedule
//           schedule_string
//           working_time_today
//           distance
//         } 
//         total_count 
//         page_info { 
//           current_page 
//           page_size 
//           total_pages 
//         } 
//       } 
//     }
//   `;
  
//   const response = await fetch(url, {
//     method: "POST",
//     headers: {
//       "Content-Type": "application/json",
//     },
//     body: JSON.stringify({ query }),
//   });

//   if (!response.ok) {
//     const errorText = await response.text();
//     throw new Error(
//       `Failed to fetch stores: ${response.status} ${response.statusText}. ${errorText}`
//     );
//   }
//   const data: AmastyResponse = await response.json();
//   const info = data.data?.searchAmStoreLocations
//   const stores = info?.items || [];
//   const totalNearbyStores = info?.total_count || 0;
//   const totalPages = info?.page_info?.total_pages || 1;
  
//   return {stores, totalNearbyStores, totalPages};
// }

// /**
//  * Fetch all store locations from Amasty
//  * @param baseUrl - The Magento store base URL
//  * @param pageSize - Number of results per page (default: 50)
//  * @param currentPage - Page number (default: 1)
//  * @returns Array of store locations
//  */
// export async function fetchAmastyStores(
//   baseUrl: string,
//   pageSize: number = 50,
//   currentPage: number = 1
// ): Promise<AmastyStoreLocation[]> {
//   if (!baseUrl) {
//     throw new Error("Base URL is required to fetch stores");
//   }

//   try {
//     const data = await executeAmastyQuery(baseUrl, "{}", pageSize, currentPage);

//     if (data.errors && data.errors.length > 0) {
//       throw new Error(`GraphQL error: ${data.errors[0]?.message || 'Unknown error'}`);
//     }

//     return data.data?.searchAmStoreLocations?.items || [];
//   } catch (error) {
//     if (error instanceof Error && error.message.includes("Failed to fetch stores")) {
//       throw error;
//     }
//     throw new Error(`Failed to fetch stores: ${error instanceof Error ? error.message : "Unknown error"}`);
//   }
// }

/**
 * Search store locations by radius from a point
 * @param baseUrl - The Magento store base URL
 * @param latitude - Center point latitude
 * @param longitude - Center point longitude
 * @param radius - Search radius (default: 25.0)
 * @param pageSize - Number of results per page (default: 10)
 * @param currentPage - Page number (default: 1)
 * @returns Array of store locations with distance
 */
export async function searchAmastyStoresByRadius(
  retailerId: string,
  latitude: number,
  longitude: number,
  radius: number = 20.0,
  pageSize: number = 10,
  currentPage: number = 1
): Promise<{ stores: AmastyStoreLocation[], totalNearbyStores: number, totalPages: number }> {
  if (!retailerId) {
    throw new Error("Retailer ID is required to search stores");
  }

  const filter = `{
    distance: {
      latitude: ${latitude},
      longitude: ${longitude},
      radius: ${radius}
    }
  }`;

  try {
    // const data = await executeAmastyQuery(baseUrl, filter, pageSize, currentPage);

    // if (data.errors && data.errors.length > 0) {
    //   throw new Error(`GraphQL error: ${data.errors[0]?.message || 'Unknown error'}`);
    // }

    const data = await fetchStoresAmasty(retailerId, filter, pageSize, currentPage);

    return { stores: data.stores || [], totalNearbyStores: data.totalNearbyStores || 0, totalPages: data.totalPages || 1 };
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch stores")) {
      throw error;
    }
    throw new Error(`Failed to search stores: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

/**
 * User location result interface
 */
export interface UserLocation {
  lat: string;
  lon: string;
  accuracy?: number;
}

/**
 * Geocoding result from Nominatim
 */
export interface GeocodingResult {
  display_name?: string;
  lat: string;
  lon: string;
  type?: string;
}

/**
 * Location detection result
 */
export interface LocationDetectionResult {
  location: UserLocation | null;
  error: string | null;
}

/**
 * Request user's GPS location with permission handling
 * @returns Promise with location or null if denied/unavailable
 */
export async function requestUserLocation(): Promise<UserLocation | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: String(position.coords.latitude),
          lon: String(position.coords.longitude),
          accuracy: position.coords.accuracy,
        });
      },
      () => {
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  });
}

/**
 * Detect user location - tries GPS first, falls back to city from address
 * @param addressCity - City from shipping address to use as fallback
 * @returns LocationDetectionResult with either GPS coordinates or city fallback
 */
export async function detectUserLocation(): Promise<LocationDetectionResult> {
  const gpsLocation = await requestUserLocation();

  if (gpsLocation) {
    return {
      location: gpsLocation,
      error: null,
    };
  }

  return {
    location: null,
    error: 'Location access denied or unavailable',
  };
}

/**
 * Fetch stores for amasty shipping - uses GPS if available, otherwise marks as TODO
 * @param baseUrl - The Magento store base URL
 * @param addressCity - City from address to use as fallback
 * @param radius - Search radius in km (converted to miles for Amasty)
 * @returns Object with stores, usesGps flag, and fallbackCity if GPS unavailable
 */
export async function fetchStoresForAmastyShipping(
  retailerId: string,
  geoLocation: GeocodingResult,
  radius: number = 25
): Promise<{
  stores: AmastyStoreLocation[];
  totalNearbyStores?: number | null;
  totalPages?: number | null;
}> {
  if (geoLocation) {
    const data = await searchAmastyStoresByRadius(
      retailerId,
      parseFloat(geoLocation.lat),
      parseFloat(geoLocation.lon),
      radius
    );
    return {
      stores : data.stores || [],
      totalNearbyStores: data.totalNearbyStores || 0,
      totalPages: data.totalPages || 1
    };
  }

  return {
    stores: [],
  };
}

export async function getCityCoords(city: string): Promise<GeocodingResult[] | null> {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(city)}&countrycodes=ZA`
  );
  const data = await response.json();

  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }

  return data;
}
